/* HH delivery-window estimator.
   A cutoff countdown, a concrete delivery date range, and a three-node
   Purchased / Shipped / Delivered timeline, sitting under Add to cart.

   Mounts into [data-hh-delivery] when the page provides one (the hh-delivery
   section), otherwise straight under the buy box's Add to cart on any product
   page - that is the rollout path to the other products.

   The date maths is pure and is exported for tests/hh-delivery.test.js;
   nothing below the EXPORT line runs under Node. */
(function () {
  'use strict';

  /* ==========================================================================
     CONFIG - the only block you should need to edit.

     TRANSIT is business days AFTER dispatch, ex Ravenhall VIC 3023, and is
     deliberately padded: ShipMarvel picks whichever of CouriersPlease / Aramex /
     Australia Post is cheapest per order, so one estimate has to cover all of
     them. Published carrier guidance is only "metro to metro 1-3 business days".
     When the committed transit matrices arrive from CouriersPlease and Aramex,
     THIS OBJECT IS THE ONLY THING THAT CHANGES.
     ========================================================================== */
  var CONFIG = {
    timeZone: 'Australia/Melbourne',
    cutoffHour: 13,
    cutoffMinute: 30,
    transit: {
      'VIC-metro':    [1, 2],
      'VIC-regional': [2, 4],
      /* 1 day is real, not a typo: Melbourne-Sydney is the busiest linehaul in
         the country and both CouriersPlease and Aramex run it overnight metro
         to metro. Confirmed by the owner 22 Sep 2026. NOTHING in this file
         floors a minimum - a 1 here means next business day after dispatch. */
      'NSW-metro':    [1, 3],
      'NSW-regional': [3, 5],
      'ACT':          [1, 3],
      'QLD-metro':    [3, 5],
      'QLD-regional': [5, 8],
      'SA-metro':     [2, 4],
      'SA-regional':  [4, 7],
      'TAS':          [3, 6],
      'WA-metro':     [5, 8],
      'WA-regional':  [7, 12],
      'NT':           [6, 10],
      'UNKNOWN':      [2, 8]
    }
  };

  /* ==========================================================================
     VICTORIAN PUBLIC HOLIDAYS - NEEDS ANNUAL REVIEW.

     Source: business.vic.gov.au/business-information/public-holidays,
     "Victorian public holidays 2026" and "... 2027", read 22 Sep 2026.

     Victoria has two state holidays that move every year - the Friday before the
     AFL Grand Final and Melbourne Cup Day - so this list CANNOT be generated.
     Add the next year from the same page each spring or dispatch dates quietly
     go wrong. Weekend dates are listed for completeness; weekends are skipped
     anyway, so only the weekday entries change a calculation.
     ========================================================================== */
  var HOLIDAYS = [
    /* ---- 2026 ---- */
    '2026-01-01', /* New Year's Day (Thu) */
    '2026-01-26', /* Australia Day (Mon) */
    '2026-03-09', /* Labour Day (Mon) */
    '2026-04-03', /* Good Friday */
    '2026-04-04', /* Saturday before Easter Sunday */
    '2026-04-05', /* Easter Sunday */
    '2026-04-06', /* Easter Monday */
    '2026-04-25', /* ANZAC Day (Sat - VIC gives no substitute) */
    '2026-06-08', /* King's Birthday (Mon) */
    '2026-09-25', /* Friday before the AFL Grand Final */
    '2026-11-03', /* Melbourne Cup (Tue) */
    '2026-12-25', /* Christmas Day (Fri) */
    '2026-12-26', /* Boxing Day (Sat) */
    '2026-12-28', /* Boxing Day substitute (Mon) */

    /* ---- 2027 ---- */
    '2027-01-01', /* New Year's Day (Fri) */
    '2027-01-26', /* Australia Day (Tue) */
    '2027-03-08', /* Labour Day (Mon) */
    '2027-03-26', /* Good Friday */
    '2027-03-27', /* Saturday before Easter Sunday */
    '2027-03-28', /* Easter Sunday */
    '2027-03-29', /* Easter Monday */
    '2027-04-25', /* ANZAC Day (Sun - VIC gives no substitute) */
    '2027-06-14', /* King's Birthday (Mon) */
    '2027-09-24', /* PROVISIONAL - Friday before the AFL Grand Final. Business
                     Victoria lists 2027 as "Subject to AFL schedule" and notes
                     it typically falls on the last Friday in September. This is
                     NOT a confirmed date. It is included because assuming a
                     holiday only ever lengthens an estimate, which is the safe
                     direction. CONFIRM when the AFL releases the 2027 fixture. */
    '2027-11-02', /* Melbourne Cup (Tue) */
    '2027-12-25', /* Christmas Day (Sat) */
    '2027-12-26', /* Boxing Day (Sun) */
    '2027-12-27', /* Christmas Day substitute (Mon) */
    '2027-12-28'  /* Boxing Day substitute (Tue) */
  ];

  var HOLIDAY_SET = {};
  for (var hi = 0; hi < HOLIDAYS.length; hi++) HOLIDAY_SET[HOLIDAYS[hi]] = true;

  /* Last year the list covers. Past this an estimate is still produced, but it
     can only skip weekends, so it may run a day optimistic. */
  var HOLIDAYS_THROUGH = 2027;

  /* ==========================================================================
     Postcode -> zone. Approximate metro definitions - the ranges couriers
     generally treat as metro. First match wins, so the specific ranges are
     listed before each state's catch-all. Worth revisiting alongside the
     carrier matrices.
     ========================================================================== */
  var POSTCODES = [
    [200, 299, 'ACT'],
    [800, 999, 'NT'],
    [1000, 1999, 'NSW-metro'],   /* Sydney PO boxes */
    [2600, 2618, 'ACT'],
    [2900, 2920, 'ACT'],
    [2000, 2249, 'NSW-metro'],
    [2555, 2574, 'NSW-metro'],
    [2745, 2770, 'NSW-metro'],
    [2000, 2999, 'NSW-regional'],
    [3000, 3207, 'VIC-metro'],
    [8000, 8499, 'VIC-metro'],   /* Melbourne PO boxes */
    [3000, 3999, 'VIC-regional'],
    [4000, 4207, 'QLD-metro'],   /* Brisbane down to the Gold Coast */
    [4500, 4519, 'QLD-metro'],
    [4550, 4575, 'QLD-metro'],   /* Sunshine Coast */
    [9000, 9099, 'QLD-metro'],   /* Brisbane PO boxes */
    [4000, 4999, 'QLD-regional'],
    [5000, 5199, 'SA-metro'],
    [5900, 5999, 'SA-metro'],    /* Adelaide PO boxes */
    [5000, 5999, 'SA-regional'],
    [6000, 6199, 'WA-metro'],
    [6800, 6999, 'WA-metro'],    /* Perth PO boxes */
    [6000, 6799, 'WA-regional'],
    [7000, 7999, 'TAS']
  ];

  /* A state on its own cannot say metro or regional, so it spans from that
     state's metro minimum to its regional maximum - honest rather than
     flattering. Entering a postcode narrows it. */
  var STATE_ZONES = {
    VIC: ['VIC-metro', 'VIC-regional'],
    NSW: ['NSW-metro', 'NSW-regional'],
    ACT: ['ACT'],
    QLD: ['QLD-metro', 'QLD-regional'],
    SA: ['SA-metro', 'SA-regional'],
    WA: ['WA-metro', 'WA-regional'],
    TAS: ['TAS'],
    NT: ['NT']
  };

  var ZONE_LABEL = {
    'VIC-metro': 'Melbourne metro',
    'VIC-regional': 'regional VIC',
    'NSW-metro': 'Sydney metro',
    'NSW-regional': 'regional NSW',
    'ACT': 'the ACT',
    'QLD-metro': 'Brisbane / Gold Coast',
    'QLD-regional': 'regional QLD',
    'SA-metro': 'Adelaide metro',
    'SA-regional': 'regional SA',
    'WA-metro': 'Perth metro',
    'WA-regional': 'regional WA',
    'TAS': 'Tasmania',
    'NT': 'the NT',
    'UNKNOWN': 'Australia-wide'
  };

  var STATE_NAME = {
    VIC: 'Victoria', NSW: 'New South Wales', ACT: 'the ACT', QLD: 'Queensland',
    SA: 'South Australia', WA: 'Western Australia', TAS: 'Tasmania', NT: 'the Northern Territory'
  };

  /* ===================== pure date maths ===================== */

  /* Civil dates are held at noon UTC so adding 86400000ms is always exactly one
     calendar day. Nothing here converts back through a timezone, so daylight
     saving cannot shift a date by one. */
  function mkDate(y, m, d) { return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function ymd(dt) { return dt.getUTCFullYear() + '-' + pad2(dt.getUTCMonth() + 1) + '-' + pad2(dt.getUTCDate()); }
  function addDays(dt, n) { return new Date(dt.getTime() + n * 86400000); }
  function isHoliday(dt) { return HOLIDAY_SET[ymd(dt)] === true; }
  function isWeekend(dt) { var w = dt.getUTCDay(); return w === 0 || w === 6; }
  function isBusinessDay(dt) { return !isWeekend(dt) && !isHoliday(dt); }
  function nextBusinessDay(dt) { var d = addDays(dt, 1); while (!isBusinessDay(d)) d = addDays(d, 1); return d; }
  function addBusinessDays(dt, n) { var d = dt; for (var i = 0; i < n; i++) d = nextBusinessDay(d); return d; }

  /* Melbourne wall-clock, whatever the visitor's device is set to. Intl carries
     the AEST/AEDT switch itself, so there is no offset here to go stale. */
  function melbourneParts(when) {
    var dt = when instanceof Date ? when : new Date(when == null ? Date.now() : when);
    var parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: CONFIG.timeZone, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(dt);
    var p = {}, i;
    for (i = 0; i < parts.length; i++) if (parts[i].type !== 'literal') p[parts[i].type] = parts[i].value;
    var hour = parseInt(p.hour, 10);
    if (hour === 24) hour = 0; /* some engines render midnight as 24 under hour12:false */
    return {
      year: parseInt(p.year, 10), month: parseInt(p.month, 10), day: parseInt(p.day, 10),
      hour: hour, minute: parseInt(p.minute, 10), second: parseInt(p.second, 10)
    };
  }

  function melbourneToday(when) { var p = melbourneParts(when); return mkDate(p.year, p.month, p.day); }

  /* Before the cutoff on a working day it goes out today, otherwise the next
     working day. 1:30pm exactly counts as missed - "order BEFORE 1:30pm". */
  function dispatchDate(when) {
    var p = melbourneParts(when);
    var today = mkDate(p.year, p.month, p.day);
    var mins = p.hour * 60 + p.minute;
    if (isBusinessDay(today) && mins < CONFIG.cutoffHour * 60 + CONFIG.cutoffMinute) return today;
    return nextBusinessDay(today);
  }

  /* Seconds left before today's cutoff, or -1 once it has gone (or today is not
     a working day). Never returns 0 or a negative, so the UI cannot count down
     into the red. Measured on the wall clock, so a 2am DST change cannot
     distort a 1:30pm countdown. */
  function secondsToCutoff(when) {
    var p = melbourneParts(when);
    if (!isBusinessDay(mkDate(p.year, p.month, p.day))) return -1;
    var left = (CONFIG.cutoffHour * 3600 + CONFIG.cutoffMinute * 60) - (p.hour * 3600 + p.minute * 60 + p.second);
    return left > 0 ? left : -1;
  }

  function transitFor(zoneId) { return CONFIG.transit[zoneId] || CONFIG.transit.UNKNOWN; }

  function deliveryWindow(when, zoneId) {
    var t = transitFor(zoneId);
    var dispatch = dispatchDate(when);
    return {
      today: melbourneToday(when),
      dispatch: dispatch,
      earliest: addBusinessDays(dispatch, t[0]),
      latest: addBusinessDays(dispatch, t[1]),
      transit: t,
      beyondHolidays: dispatch.getUTCFullYear() > HOLIDAYS_THROUGH
    };
  }

  /* ===================== zones ===================== */

  function zoneFromPostcode(pc) {
    var n = parseInt(String(pc).replace(/\D/g, ''), 10);
    if (!(n >= 0 && n <= 9999)) return null;
    for (var i = 0; i < POSTCODES.length; i++) {
      if (n >= POSTCODES[i][0] && n <= POSTCODES[i][1]) return POSTCODES[i][2];
    }
    return null;
  }

  function stateOfZone(id) {
    for (var s in STATE_ZONES) {
      if (!Object.prototype.hasOwnProperty.call(STATE_ZONES, s)) continue;
      for (var i = 0; i < STATE_ZONES[s].length; i++) if (STATE_ZONES[s][i] === id) return s;
    }
    return null;
  }

  function normState(s) {
    if (!s) return null;
    var k = String(s).trim().toUpperCase();
    var byName = {
      'VICTORIA': 'VIC', 'NEW SOUTH WALES': 'NSW', 'QUEENSLAND': 'QLD',
      'SOUTH AUSTRALIA': 'SA', 'WESTERN AUSTRALIA': 'WA', 'TASMANIA': 'TAS',
      'NORTHERN TERRITORY': 'NT', 'AUSTRALIAN CAPITAL TERRITORY': 'ACT'
    };
    if (byName[k]) return byName[k];
    return STATE_ZONES[k] ? k : null;
  }

  /* Always returns a zone - an unresolved visitor gets the Australia-wide row
     rather than a guessed state. */
  function resolveZone(input) {
    var pc = input && input.postcode ? String(input.postcode).replace(/\D/g, '') : '';
    var st = normState(input && input.state);
    var t, i;

    if (pc.length === 4) {
      var id = zoneFromPostcode(pc);
      if (id) {
        t = transitFor(id);
        return { id: id, label: ZONE_LABEL[id], min: t[0], max: t[1], state: stateOfZone(id), postcode: pc, precision: 'postcode' };
      }
    }
    if (st) {
      var ids = STATE_ZONES[st], min = Infinity, max = 0;
      for (i = 0; i < ids.length; i++) {
        t = transitFor(ids[i]);
        if (t[0] < min) min = t[0];
        if (t[1] > max) max = t[1];
      }
      return {
        id: ids.length === 1 ? ids[0] : st,
        label: ids.length === 1 ? ZONE_LABEL[ids[0]] : STATE_NAME[st],
        min: min, max: max, state: st, postcode: '', precision: 'state'
      };
    }
    var u = CONFIG.transit.UNKNOWN;
    return { id: 'UNKNOWN', label: ZONE_LABEL.UNKNOWN, min: u[0], max: u[1], state: null, postcode: '', precision: 'none' };
  }

  /* ===================== formatting ===================== */

  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  function fmtDay(dt) { return MON[dt.getUTCMonth()] + ' ' + dt.getUTCDate(); }
  function fmtDow(dt) { return DOW[dt.getUTCDay()] + ' ' + fmtDay(dt); }
  function fmtRange(a, b) {
    if (ymd(a) === ymd(b)) return fmtDay(a);                    /* one day, not a range */
    if (a.getUTCMonth() === b.getUTCMonth()) return MON[a.getUTCMonth()] + ' ' + a.getUTCDate() + ' - ' + b.getUTCDate();
    return fmtDay(a) + ' - ' + fmtDay(b);                       /* Sep 30 - Oct 2 */
  }
  function fmtCountdown(sec) {
    var mins = Math.ceil(sec / 60), h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
  }

  var CORE = {
    CONFIG: CONFIG, HOLIDAYS: HOLIDAYS, HOLIDAYS_THROUGH: HOLIDAYS_THROUGH,
    mkDate: mkDate, ymd: ymd, addDays: addDays,
    isHoliday: isHoliday, isBusinessDay: isBusinessDay,
    nextBusinessDay: nextBusinessDay, addBusinessDays: addBusinessDays,
    melbourneParts: melbourneParts, melbourneToday: melbourneToday,
    dispatchDate: dispatchDate, secondsToCutoff: secondsToCutoff,
    deliveryWindow: deliveryWindow,
    zoneFromPostcode: zoneFromPostcode, resolveZone: resolveZone, normState: normState,
    fmtDay: fmtDay, fmtDow: fmtDow, fmtRange: fmtRange, fmtCountdown: fmtCountdown
  };

  /* ===================== EXPORT - Node stops here ===================== */
  if (typeof module !== 'undefined' && module.exports) { module.exports = CORE; return; }
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  window.HHDelivery = CORE;

  /* ===================== browser ===================== */

  var NAVY = '#1E326C', INK = '#16234A', MUTE = '#5A6786', LINE = '#DCE3F0', SKY = '#EAF2FC';
  var LS_MANUAL = 'hh_dw_manual';  /* what the visitor picked - wins, never expires */
  var LS_GEO = 'hh_dw_geo';        /* cached lookup */
  var GEO_TTL = 7 * 24 * 3600 * 1000;

  function lsGet(k) { try { var v = window.localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }

  /* Intl fixes a device set to the wrong TIMEZONE, but not one set to the wrong
     TIME. Shopify's own response carries a Date header, so the first same-origin
     request doubles as a clock check and everything after it runs off now().
     Until that lands we use the device clock, which is right for almost everyone. */
  var clockSkew = 0;
  function now() { return Date.now() + clockSkew; }
  function setSkew(serverMs) {
    if (!serverMs) return;
    var d = serverMs - Date.now();
    /* ignore sub-minute noise, and ignore anything absurd rather than trust it */
    if (Math.abs(d) > 60000 && Math.abs(d) < 3153600000000) clockSkew = d;
  }

  var css = ''
    + '.hh-dw{border:1px solid ' + LINE + ';border-radius:12px;padding:11px 13px 12px;margin:12px 0 0;'
    + 'font-family:inherit;color:' + INK + ';background:#fff}'
    + '.hh-dw-head{margin:0 0 10px;font-size:12.5px;line-height:1.4;font-weight:600;display:flex;gap:7px;align-items:flex-start}'
    + '.hh-dw-head b{font-weight:800;color:' + NAVY + '}'
    + '.hh-dw-head svg{flex:none;margin-top:1px}'
    /* three nodes, with the rail drawn between the icon centres only */
    + '.hh-dw-track{display:grid;grid-template-columns:repeat(3,1fr);position:relative;margin:0 0 9px}'
    + '.hh-dw-track::before{content:"";position:absolute;left:16.6%;right:16.6%;top:13px;height:2px;background:' + LINE + ';z-index:0}'
    + '.hh-dw-n{display:grid;justify-items:center;gap:3px;position:relative;z-index:1;text-align:center}'
    + '.hh-dw-ico{width:28px;height:28px;border-radius:999px;display:grid;place-items:center;'
    + 'background:#fff;border:1.5px solid ' + LINE + ';color:' + MUTE + '}'
    + '.hh-dw-n.is-on .hh-dw-ico{background:' + NAVY + ';border-color:' + NAVY + ';color:#fff}'
    + '.hh-dw-d{font-size:11.5px;font-weight:800;color:' + NAVY + ';font-variant-numeric:tabular-nums;margin-top:3px;line-height:1.25}'
    + '.hh-dw-l{font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:' + MUTE + '}'
    + '.hh-dw-foot{margin:0;font-size:11px;line-height:1.4;color:' + MUTE + ';font-weight:600}'
    + '.hh-dw-change{border:0;background:none;padding:0;font:inherit;font-size:11px;font-weight:800;'
    + 'color:' + NAVY + ';text-decoration:underline;cursor:pointer}'
    + '.hh-dw-form{margin:9px 0 0;padding:9px 10px;background:' + SKY + ';border-radius:10px;display:grid;gap:6px}'
    + '.hh-dw-lab{font-size:10.5px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:' + MUTE + '}'
    + '.hh-dw-row{display:flex;gap:6px}'
    + '.hh-dw-in,.hh-dw-sel{flex:1;min-width:0;border:1px solid ' + LINE + ';border-radius:8px;padding:8px 10px;'
    + 'font:inherit;font-size:13px;color:' + INK + ';background:#fff}'
    + '.hh-dw-go{border:0;border-radius:8px;background:' + NAVY + ';color:#fff;font:inherit;font-size:12px;'
    + 'font-weight:800;padding:8px 14px;cursor:pointer;flex:none}'
    + '.hh-dw-err{margin:0;font-size:11.5px;font-weight:700;color:#B3261E}'
    + '.hh-dw-intl{margin:0;font-size:12.5px;line-height:1.45;font-weight:600}'
    + '.hh-dw-intl a{color:' + NAVY + ';font-weight:800}'
    /* below ~340px the three labels start to crowd; shrink rather than wrap */
    + '@media (max-width:359px){.hh-dw-d{font-size:10.5px}.hh-dw-l{font-size:9px}}';

  var st = document.createElement('style');
  st.id = 'hh-delivery-css';
  st.textContent = css;
  document.head.appendChild(st);

  var SVG = {
    clock: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="' + NAVY + '" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    bag: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 7h12l-1 13H7L6 7Z"/><path d="M9 7V5a3 3 0 0 1 6 0v2"/></svg>',
    truck: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 7h11v9H2z"/><path d="M13 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/></svg>',
    pin: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"/><circle cx="12" cy="10" r="2.5"/></svg>'
  };

  var STATE_OPTS = ['VIC', 'NSW', 'ACT', 'QLD', 'SA', 'WA', 'TAS', 'NT'];

  var node = null, formOpen = false, lastSig = '';
  var view = { country: null, zone: resolveZone(null), manual: false };

  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  function build() {
    node = document.createElement('div');
    node.className = 'hh-dw';
    node.setAttribute('role', 'group');
    node.setAttribute('aria-label', 'Delivery estimate');
    node.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('.hh-dw-change') : null;
      if (!b) return;
      e.preventDefault();
      formOpen = !formOpen;
      lastSig = '';
      paint();
      if (formOpen) { var i = node.querySelector('.hh-dw-in'); if (i) i.focus(); }
    });
    node.addEventListener('submit', function (e) {
      if (!e.target.classList.contains('hh-dw-form')) return;
      e.preventDefault();
      submit();
    });
    return node;
  }

  function submit() {
    var pcEl = node.querySelector('.hh-dw-in'), stEl = node.querySelector('.hh-dw-sel');
    var pc = pcEl ? pcEl.value.replace(/\D/g, '') : '';
    var stv = stEl ? stEl.value : '';
    var errEl = node.querySelector('.hh-dw-err');

    if (pc) {
      if (pc.length !== 4 || !zoneFromPostcode(pc)) {
        if (errEl) { errEl.textContent = 'That does not look like an Australian postcode.'; errEl.hidden = false; }
        return;
      }
      view.zone = resolveZone({ postcode: pc });
    } else if (stv) {
      view.zone = resolveZone({ state: stv });
    } else {
      if (errEl) { errEl.textContent = 'Enter a postcode or pick a state.'; errEl.hidden = false; }
      return;
    }
    view.manual = true;
    view.country = 'AU';   /* they told us where they are; believe them over geo-IP */
    lsSet(LS_MANUAL, { postcode: view.zone.postcode, state: view.zone.state });
    formOpen = false;
    lastSig = '';
    paint();
  }

  function headHTML(win) {
    var sec = secondsToCutoff(now());
    var range = '<b>' + fmtRange(win.earliest, win.latest) + '</b>';
    if (sec > 0) {
      return SVG.clock + '<span>Order within <b>' + fmtCountdown(sec) + '</b>, and you&rsquo;ll get it ' + range + '</span>';
    }
    /* past the cutoff (or a weekend / public holiday) - no zero, no negative */
    return SVG.clock + '<span>Order now &mdash; ships <b>' + fmtDow(win.dispatch) + '</b>, and you&rsquo;ll get it ' + range + '</span>';
  }

  function formHTML() {
    var opts = '<option value="">Select a state</option>';
    for (var i = 0; i < STATE_OPTS.length; i++) {
      var s = STATE_OPTS[i];
      opts += '<option value="' + s + '"' + (view.zone.state === s ? ' selected' : '') + '>' + esc(STATE_NAME[s]) + '</option>';
    }
    return '<form class="hh-dw-form">'
      + '<label class="hh-dw-lab" for="hh-dw-pc">Your postcode</label>'
      + '<div class="hh-dw-row">'
      + '<input id="hh-dw-pc" class="hh-dw-in" type="text" inputmode="numeric" autocomplete="postal-code" maxlength="4" placeholder="e.g. 3000" value="' + esc(view.zone.postcode || '') + '">'
      + '<button type="submit" class="hh-dw-go">Update</button>'
      + '</div>'
      + '<label class="hh-dw-lab" for="hh-dw-st">or pick your state</label>'
      + '<select id="hh-dw-st" class="hh-dw-sel">' + opts + '</select>'
      + '<p class="hh-dw-err" hidden></p>'
      + '</form>';
  }

  function paint() {
    if (!node) return;

    if (view.country && view.country !== 'AU') {
      var ih = '<p class="hh-dw-intl">' + SVG.pin + ' Shipping outside Australia? '
        + '<a href="/pages/contact">Contact us for delivery times</a></p>';
      if (node.innerHTML !== ih) node.innerHTML = ih;
      lastSig = 'intl';
      return;
    }

    var win = deliveryWindow(now(), view.zone.id);
    /* the state-only case spans two rows, so take min/max off the zone, not the id */
    win.earliest = addBusinessDays(win.dispatch, view.zone.min);
    win.latest = addBusinessDays(win.dispatch, view.zone.max);

    var foot = view.zone.precision === 'none'
      ? 'Australia-wide estimate. '
      : 'Estimate for ' + esc(view.zone.label) + (view.zone.postcode ? ' (' + esc(view.zone.postcode) + ')' : '') + '. ';
    /* one label for both cases - the sentence before it already says whether we
       have a zone or not, so the button only has to offer the action */
    foot += '<button type="button" class="hh-dw-change">Enter your postcode</button>';

    var html = '<p class="hh-dw-head">' + headHTML(win) + '</p>'
      + '<div class="hh-dw-track">'
      + '<div class="hh-dw-n is-on"><span class="hh-dw-ico">' + SVG.bag + '</span>'
      + '<span class="hh-dw-d">' + fmtDay(win.today) + '</span><span class="hh-dw-l">Purchased</span></div>'
      + '<div class="hh-dw-n"><span class="hh-dw-ico">' + SVG.truck + '</span>'
      + '<span class="hh-dw-d">' + fmtDay(win.dispatch) + '</span><span class="hh-dw-l">Shipped</span></div>'
      + '<div class="hh-dw-n"><span class="hh-dw-ico">' + SVG.pin + '</span>'
      + '<span class="hh-dw-d">' + fmtRange(win.earliest, win.latest) + '</span><span class="hh-dw-l">Delivered</span></div>'
      + '</div>'
      + '<p class="hh-dw-foot">' + foot + '</p>'
      + (formOpen ? formHTML() : '');

    /* Entities come back out of innerHTML as literal characters, so compare a
       signature instead of the markup - otherwise this rebuilds every tick and
       eats the click that opened the form. */
    var sig = html.length + '|' + fmtDay(win.today) + '|' + fmtDay(win.dispatch) + '|'
      + fmtRange(win.earliest, win.latest) + '|' + (secondsToCutoff(now()) > 0 ? fmtCountdown(secondsToCutoff(now())) : 'closed')
      + '|' + view.zone.id + '|' + (formOpen ? 'open' : 'shut');
    if (sig === lastSig) return;
    lastSig = sig;
    node.innerHTML = html;
  }

  /* Place it under Add to cart, above the FAQ. Only ever moves the node when it
     is not already where it belongs, so it cannot fight the FAQ layer. */
  function place() {
    var host = document.querySelector('[data-hh-delivery]');
    if (host) {
      if (!host.contains(node)) host.appendChild(node);
      return true;
    }
    var add = document.querySelector('[data-hh-tiers] .hh-t-add') || document.querySelector('.product-form__buttons');
    if (!add) return false;
    /* Anchor to Add to cart, NOT to the FAQ. The hazard report card is lifted in
       between the two by hh-buybox-plus, and if both this and the report each
       insisted on being the FAQ's immediate previous sibling they would shove
       each other back and forth forever through their MutationObservers. */
    if (add.nextElementSibling !== node) add.parentNode.insertBefore(node, add.nextSibling);
    return true;
  }

  /* ---------- where is the visitor ---------- */

  function getJSON(url, ms) {
    return new Promise(function (resolve) {
      var done = false, ctl = null, timer;
      try { ctl = new AbortController(); } catch (e) { ctl = null; }
      timer = setTimeout(function () { if (!done) { done = true; if (ctl) { try { ctl.abort(); } catch (e) {} } resolve(null); } }, ms);
      var opts = ctl ? { signal: ctl.signal, credentials: 'omit' } : { credentials: 'omit' };
      window.fetch(url, opts).then(function (r) {
        if (r) { var h = r.headers && r.headers.get && r.headers.get('date'); if (h) { var t = Date.parse(h); if (t) setSkew(t); } }
        return r && r.ok ? r.json() : null;
      }).then(function (j) {
        if (done) return;
        done = true; clearTimeout(timer); resolve(j);
      })['catch'](function () {
        if (done) return;
        done = true; clearTimeout(timer); resolve(null);
      });
    });
  }

  /* Shopify's own geolocation. Same-origin, no third party, but it only ever
     returns a COUNTRY - there is no state in the payload - so it answers the
     "is this an Australian shopper" question and nothing more. */
  function detectCountry() {
    return getJSON('/browsing_context_suggestions.json', 2500).then(function (j) {
      var c = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
      return c ? String(c).toUpperCase() : null;
    });
  }

  /* State and postcode need an IP lookup. Two providers, short timeouts, and a
     silent fall through to the Australia-wide row - an ad blocker eating these
     must never leave a broken element on the page. */
  var PROVIDERS = [
    { url: 'https://ipwho.is/', map: function (j) {
      if (!j || j.success === false) return null;
      return { country: j.country_code, state: j.region_code || j.region, postcode: j.postal };
    } },
    { url: 'https://get.geojs.io/v1/ip/geo.json', map: function (j) {
      if (!j || !j.country_code) return null;
      return { country: j.country_code, state: j.region, postcode: '' };
    } }
  ];

  function lookupIp(i) {
    i = i || 0;
    if (i >= PROVIDERS.length) return Promise.resolve(null);
    return getJSON(PROVIDERS[i].url, 3500).then(function (j) {
      var g = null;
      try { g = PROVIDERS[i].map(j); } catch (e) { g = null; }
      if (g && (g.state || g.postcode)) return g;
      return lookupIp(i + 1);
    });
  }

  function applyGeo(g) {
    if (!g) return;
    if (g.country) view.country = String(g.country).toUpperCase();
    if (view.country && view.country !== 'AU') return;
    var z = resolveZone({ state: g.state, postcode: g.postcode });
    if (z.precision !== 'none') view.zone = z;
  }

  function detect() {
    detectCountry().then(function (c) {
      if (c) view.country = c;
      if (c && c !== 'AU') {
        lsSet(LS_GEO, { country: c, ts: Date.now() });
        paint();
        return null;
      }
      return lookupIp(0).then(function (g) {
        if (g) {
          lsSet(LS_GEO, { country: (g.country || c || 'AU'), state: g.state, postcode: g.postcode, ts: Date.now() });
          applyGeo(g);
        }
        paint();
        return null;
      });
    })['catch'](function () { paint(); });
  }

  function boot() {
    build();
    if (!place()) return;

    var manual = lsGet(LS_MANUAL);
    if (manual && (manual.postcode || manual.state)) {
      view.zone = resolveZone(manual);
      view.manual = true;
      view.country = 'AU';
      paint();
    } else {
      var geo = lsGet(LS_GEO);
      if (geo && geo.ts && (Date.now() - geo.ts) < GEO_TTL) {
        applyGeo(geo);
        paint();
      } else {
        paint();   /* render the Australia-wide estimate now; refine when geo lands */
        detect();
      }
    }

    /* every 30s keeps the displayed minute honest; the signature check means
       nothing is touched unless the text actually changed */
    setInterval(paint, 30000);

    var root = document.querySelector('[data-hh-tiers]');
    if (root) new MutationObserver(function () { place(); paint(); }).observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
